// Pure helpers for the database agents page and the agent installers:
// direct-access status, relative "checked" text, and the db.env builder.

export const PROXYSQL_ADMIN_USER = "admin";
export const PROXYSQL_ADMIN_ADDR = "127.0.0.1:6032";

const ALNUM = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

const getCrypto = () => {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (!c || typeof c.getRandomValues !== "function") {
    throw new Error("Secure random numbers are not available in this browser.");
  }
  return c;
};

/**
 * Random letters and digits from crypto.getRandomValues. Rejection sampling
 * keeps every character equally likely (256 is not a multiple of 62).
 */
export const generateAlnumSecret = (length = 32) => {
  const c = getCrypto();
  const limit = 256 - (256 % ALNUM.length); // 248
  let out = "";
  while (out.length < length) {
    const buf = new Uint8Array(length * 2);
    c.getRandomValues(buf);
    for (let i = 0; i < buf.length && out.length < length; i++) {
      if (buf[i] < limit) out += ALNUM[buf[i] % ALNUM.length];
    }
  }
  return out;
};

/**
 * db.env text: the installer's base lines, then the install credential and the
 * ProxySQL admin login. Refuses to build without a credential, so a db.env
 * without one is never downloaded.
 */
export const buildDbEnv = ({ base = [], clientId, clientSecret, proxysqlAdminPassword }) => {
  if (!clientId || !clientSecret) {
    throw new Error("An install credential is required to build db.env.");
  }
  if (!proxysqlAdminPassword) {
    throw new Error("A ProxySQL admin password is required to build db.env.");
  }
  return [
    ...base,
    `PROXY_CLIENT_ID=${clientId}`,
    `PROXY_CLIENT_SECRET=${clientSecret}`,
    `PROXYSQL_ADMIN_USER=${PROXYSQL_ADMIN_USER}`,
    `PROXYSQL_ADMIN_PASSWORD=${proxysqlAdminPassword}`,
    `PROXYSQL_ADMIN_ADDR=${PROXYSQL_ADMIN_ADDR}`,
  ].join("\n") + "\n";
};

/** "just now", "2 min ago", "3 h ago", "4 days ago"; "" when there is no time. */
export const relativeTime = (iso, now = Date.now()) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? "day" : "days"} ago`;
};

/** "checked 2 min ago", or "" when the agent has not reported yet. */
export const checkedText = (iso, now = Date.now()) => {
  const rel = relativeTime(iso, now);
  return rel ? `checked ${rel}` : "";
};

export const DIRECT_ACCESS = {
  protected: { label: "Protected", tone: "success" },
  exposed: { label: "Exposed", tone: "danger" },
  unknown: { label: "Unknown", tone: "neutral" },
};

/** Status from the API (any casing, possibly empty) -> protected | exposed | unknown. */
export const directAccessKey = (status) => {
  const k = String(status || "").trim().toLowerCase();
  return DIRECT_ACCESS[k] ? k : "unknown";
};

export const EXPOSED_WARNING =
  "Whoever can reach ProxySQL's admin interface can read every managed password.";

/** A label for the install credential when the admin leaves the name empty. */
export const defaultInstallName = (platform, extra, now = new Date()) => {
  const date = now.toISOString().slice(0, 10);
  return [platform, extra, date].filter(Boolean).join(" ");
};
